import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BroomIcon, PlusIcon, CheckCircleIcon, PlayCircleIcon } from "@phosphor-icons/react";
import { HK } from "@/constants/testIds";

const priorityColor = { low: "text-muted-foreground", normal: "text-primary", high: "text-destructive" };

export default function Housekeeping() {
  const { activeId } = useProperty();
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ room_id: "", task: "cleaning", priority: "normal", notes: "", assigned_to: "" });

  const load = () => {
    if (!activeId) return;
    api.get("/housekeeping", { params: { property_id: activeId } }).then((r) => setTasks(r.data));
    api.get("/rooms", { params: { property_id: activeId } }).then((r) => setRooms(r.data));
    api.get("/users", { silent: true }).then((r) => setStaff(r.data.filter((u) => u.role === "housekeeping"))).catch(() => {});
  };
  useEffect(() => { load(); }, [activeId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/housekeeping", { ...form, property_id: activeId, assigned_to: form.assigned_to || null });
      toast.success("Task created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const setStatus = async (t, status) => {
    await api.patch(`/housekeeping/${t.id}`, { status });
    toast.success(`Marked ${status.replace("_", " ")}`);
    load();
  };

  const columns = [
    { key: "pending", label: "Pending" },
    { key: "in_progress", label: "In progress" },
    { key: "done", label: "Done" },
  ];

  return (
    <div>
      <PageHeader
        overline="Operations"
        title="Housekeeping"
        description="Track cleaning, maintenance and inspection tasks by room."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid={HK.newBtn}><PlusIcon size={16} className="mr-1" />New task</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Room</Label>
                  <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Room" /></SelectTrigger>
                    <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.room_type}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Task</Label>
                    <Select value={form.task} onValueChange={(v) => setForm({ ...form, task: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cleaning">Cleaning</SelectItem>
                        <SelectItem value="turndown">Turndown</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="inspection">Inspection</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {staff.length > 0 && (
                  <div>
                    <Label>Assign to</Label>
                    <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter><Button type="submit">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {columns.map((col) => {
          const list = tasks.filter((t) => t.status === col.key);
          return (
            <div key={col.key}>
              <div className="flex items-center justify-between mb-3">
                <p className="label-overline">{col.label}</p>
                <span className="font-mono-plex text-xs text-muted-foreground">{list.length}</span>
              </div>
              <div className="space-y-3">
                {list.map((t) => (
                  <Card key={t.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">Room {t.room?.number} · <span className="text-muted-foreground text-xs">{t.room?.room_type}</span></p>
                        <p className="text-xs text-muted-foreground mt-1 capitalize">{t.task}</p>
                      </div>
                      <BroomIcon size={18} className={priorityColor[t.priority]} weight="duotone" />
                    </div>
                    {t.notes && <p className="text-xs text-muted-foreground mt-2">{t.notes}</p>}
                    {t.assignee && <Badge variant="outline" className="mt-3 text-[10px]">Assigned: {t.assignee.name}</Badge>}
                    <div className="mt-3 flex gap-2">
                      {t.status === "pending" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "in_progress")} data-testid={HK.markInProgress}><PlayCircleIcon size={14} className="mr-1" />Start</Button>}
                      {t.status !== "done" && <Button size="sm" onClick={() => setStatus(t, "done")} data-testid={HK.markDone}><CheckCircleIcon size={14} className="mr-1" />Done</Button>}
                    </div>
                  </Card>
                ))}
                {list.length === 0 && <p className="text-xs text-muted-foreground italic px-1">No tasks</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
